import { useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import {
  Pencil, Mail, Phone, MapPin, CreditCard, ShieldAlert, Plane, Lock,
  Calendar, Car, Fingerprint,
} from "lucide-react";

const v = (x) => (x === "" || x == null ? "—" : x);
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return d; }
};

function ContactDialog({ open, onOpenChange, profile, onSaved }) {
  const [form, setForm] = useState({
    phone: profile.phone || "", address: profile.address || "",
    iban: profile.iban || "", emergency_contact: profile.emergency_contact || "",
    birth_date: profile.birth_date || "", nif: profile.nif || "", car_info: profile.car_info || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/hr/me/profile", form);
      toast.success("Datos actualizados");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar mis datos</DialogTitle>
          <DialogDescription>Estos datos los gestionas tú.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha de nacimiento</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} data-testid="ficha-birthdate-input" /></div>
            <div className="space-y-1.5"><Label>DNI</Label><Input value={form.nif} onChange={(e) => set("nif", e.target.value)} placeholder="00000000X" data-testid="ficha-dni-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ficha-phone-input" /></div>
          <div className="space-y-1.5"><Label>Matrícula y tipo de coche</Label><Input value={form.car_info} onChange={(e) => set("car_info", e.target.value)} placeholder="1234 ABC · Seat León gris" data-testid="ficha-car-input" /></div>
          <div className="space-y-1.5"><Label>Dirección</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="ficha-address-input" /></div>
          <div className="space-y-1.5"><Label>IBAN</Label><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} data-testid="ficha-iban-input" /></div>
          <div className="space-y-1.5"><Label>Contacto de emergencia</Label><Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} data-testid="ficha-emergency-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="ficha-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ icon: Icon, label, value, locked }) {
  return (
    <div className="flex items-start gap-3 p-5 border-b border-border">
      <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground flex items-center gap-1">{label} {locked && <Lock className="h-3 w-3" />}</div>
        <div className="text-sm font-medium mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

export default function MiFicha() {
  const { data: p, loading, refetch } = useFetch("/hr/me/profile");
  const [dialogOpen, setDialogOpen] = useState(false);

  if (loading || !p) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="mi-ficha-page">
      <PageHeader title="Mi ficha" subtitle="Tus datos personales">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="edit-contact-btn">
          <Pencil className="h-4 w-4" /> Editar mis datos
        </Button>
      </PageHeader>

      <Card className="border-border shadow-none p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <Avatar className="h-20 w-20">
            <AvatarImage src={p.picture} alt={p.name} />
            <AvatarFallback className="text-xl bg-primary/10 text-primary">{p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-2xl tracking-tight">{p.name}</h2>
            <p className="text-muted-foreground mt-1">{v(p.position)} · {p.role}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {p.department && <Badge variant="outline" className="border-transparent bg-primary/12 text-primary font-medium">{p.department}</Badge>}
              <Badge variant="outline" className="border-transparent bg-gold/15 text-[#a9741c] dark:text-gold font-medium gap-1"><Plane className="h-3 w-3" /> {p.vacation_days_year} días/año</Badge>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="p-5 pb-3 border-b border-border">
          <h3 className="font-heading font-bold text-base">Datos personales y de contacto</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Puedes editarlos tú mismo</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <Field icon={Mail} label="Email corporativo" value={v(p.email)} />
          <Field icon={Phone} label="Teléfono" value={v(p.phone)} />
          <Field icon={Calendar} label="Fecha de nacimiento" value={fmtDate(p.birth_date)} />
          <Field icon={Fingerprint} label="DNI" value={v(p.nif)} />
          <Field icon={Car} label="Matrícula y tipo de coche" value={v(p.car_info)} />
          <Field icon={MapPin} label="Dirección" value={v(p.address)} />
          <Field icon={CreditCard} label="IBAN" value={v(p.iban)} />
          <Field icon={ShieldAlert} label="Contacto de emergencia" value={v(p.emergency_contact)} />
        </div>
      </Card>

      {dialogOpen && <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} profile={p} onSaved={refetch} />}
    </div>
  );
}
